import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import API_BASE_URL from "../config";
import "react-phone-input-2/lib/bootstrap.css";
import PhoneInput from "react-phone-input-2";

// Simple slugify utility
const slugify = (s) =>
  s
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");

// Custom CSS for professional and responsive styling
const customStyles = `
  .register-company-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem;
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid #e0e0e0;
  }
  .register-company-container h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 1.5rem;
  }
  .register-company-section-header {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 1.5rem 0 1rem;
  }
  .register-company-form .form-label {
    font-weight: 500;
    color: #4a4a4a;
  }
  .register-company-form .form-control,
  .register-company-form .form-select {
    border-radius: 0.375rem;
    border: 1px solid #ced4da;
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }
  .register-company-form .form-control:focus,
  .register-company-form .form-select:focus {
    border-color: #0066cc;
    box-shadow: 0 0 0 0.2rem rgba(0, 102, 204, 0.25);
  }
  .register-company-form .btn-primary {
    background-color: #0066cc;
    border-color: #0066cc;
    border-radius: 0.375rem;
    padding: 0.5rem 1.5rem;
    font-size: 0.9rem;
    transition: background-color 0.2s ease-in-out;
  }
  .register-company-form .btn-primary:hover {
    background-color: #0055a8;
    border-color: #0055a8;
  }
  .register-company-form .btn-primary:disabled {
    background-color: #6c757d;
    border-color: #6c757d;
  }
  .register-company-phone-container {
    display: flex;
    align-items: center;
    width: 100%;
  }
  .register-company-phone-input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
    border: 1px solid #ced4da;
    height: 38px;
    line-height: 1.5;
    font-size: 0.9rem;
  }
  .register-company-phone-button {
    border-radius: 0.375rem 0 0 0.375rem;
    border: 1px solid #ced4da;
    border-right: none;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 38px;
    padding: 0 0.5rem;
    background-color: #fff;
  }
  .register-company-phone-button .flag {
    margin: 0;
    vertical-align: middle;
  }
  @media (max-width: 576px) {
    .register-company-container {
      padding: 1rem;
    }
    .register-company-container h2 {
      font-size: 1.5rem;
    }
    .register-company-section-header {
      font-size: 1.1rem;
    }
    .register-company-form .form-label {
      font-size: 0.85rem;
    }
    .register-company-form .form-control,
    .register-company-form .form-select {
      font-size: 0.85rem;
    }
    .register-company-form .btn-primary {
      width: 100%;
      padding: 0.5rem;
      font-size: 0.85rem;
    }
  }
`;

// Inject custom styles into the document
const styleSheet = document.createElement("style");
styleSheet.innerText = customStyles;
document.head.appendChild(styleSheet);

export default function RegisterCompany() {
  const [form, setForm] = useState({
    name: "",
    estd: "",
    pan_number: "",
    gst_number: "",
    owner_name: "",
    company_email: "",
    phone: "+91", // Default to +91
    website: "",
    city: "",
    state: "",
    country: "",
    address: "",
    landmark: "",
    category_id: "",
    subcategory_id: "",
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [logo, setLogo] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load categories
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/categories`)
      .then((res) => setCategories(res.data))
      .catch(() => setError("Failed to load categories"));
  }, []);

  // Load subcategories when category_id changes
  useEffect(() => {
    if (form.category_id) {
      axios
        .get(`${API_BASE_URL}/categories/${form.category_id}/subcategories`)
        .then((res) => setSubcategories(res.data))
        .catch(() => setError("Failed to load subcategories"));
    } else {
      setSubcategories([]);
    }
  }, [form.category_id]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    setLogo(e.target.files[0]);
  };

  // Client-side validation
  const validate = () => {
    if (!form.name.trim()) return "Company name is required";
    if (!form.owner_name.trim()) return "Owner name is required";
    if (!form.company_email.match(/^\S+@\S+\.\S+$/))
      return "Valid company email required";
    if (
      !form.gst_number.match(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
      )
    )
      return "Valid GST number required (e.g., 22AAAAA0000A1Z5)";
    if (!form.city || !form.state || !form.country || !form.address)
      return "Address, city, state, and country are required to prevent duplicates";
    if (!form.category_id) return "Category is required";
    if (!form.subcategory_id) return "Subcategory is required";
    if (!/^\+\d{8,15}$/.test(form.phone))
      return "Please enter a valid phone number with country code.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();

      // Append text fields
      Object.keys(form).forEach((key) => {
        fd.append(key, form[key]);
      });

      // Slug
      fd.append("slug", slugify(form.name));

      // Append logo file if selected
      if (logo) {
        fd.append("logo", logo);
      }

      const res = await axios.post(`${API_BASE_URL}/companies`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccess("Company created — please check company email to confirm.");
      setForm({
        name: "",
        estd: "",
        pan_number: "",
        gst_number: "",
        owner_name: "",
        company_email: "",
        phone: "+91", // Reset to +91
        website: "",
        city: "",
        state: "",
        country: "",
        address: "",
        landmark: "",
        category_id: "",
        subcategory_id: "",
      });
      setLogo(null);
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to create company");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-4 register-company-container">
      <h2>Register Your Business</h2>
      {error && <Alert variant="danger" className="rounded-3">{error}</Alert>}
      {success && <Alert variant="success" className="rounded-3">{success}</Alert>}
      <Form onSubmit={handleSubmit} noValidate className="register-company-form">
        <h4 className="register-company-section-header">Company Details</h4>
        <Row>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Company Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Owner Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              name="owner_name"
              value={form.owner_name}
              onChange={handleChange}
              required
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Company Email <span className="text-danger">*</span></Form.Label>
            <Form.Control
              name="company_email"
              value={form.company_email}
              onChange={handleChange}
              type="email"
              required
            />
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
            <PhoneInput
              country={"in"}
              value={form.phone}
              onChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  phone: value ? `+${value}` : "+91",
                }))
              }
              inputProps={{
                name: "phone",
                required: true,
                className: "form-control register-company-phone-input",
              }}
              enableSearch={true}
              containerClass="register-company-phone-container"
              inputClass="register-company-phone-input"
              buttonClass="register-company-phone-button"
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>GST Number </Form.Label>
            {/* <span className="text-danger">*</span> required*/}
            <Form.Control
              name="gst_number"
              value={form.gst_number}
              onChange={handleChange}
              pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}"
              title="Enter a valid GST number (e.g., 22AAAAA0000A1Z5)"
            />
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>PAN Number</Form.Label>
            <Form.Control
              name="pan_number"
              value={form.pan_number}
              onChange={handleChange}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Established (Year)</Form.Label>
            <Form.Control
              name="estd"
              value={form.estd}
              onChange={handleChange}
              type="number"
              min="1800"
              max="2099"
            />
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Website (Optional)</Form.Label>
            <Form.Control
              name="website"
              value={form.website}
              onChange={handleChange}
            />
          </Col>
        </Row>

        <h4 className="register-company-section-header">Address Details</h4>
        <Row>
          <Col xs={12} className="mb-3">
            <Form.Label>Address <span className="text-danger">*</span></Form.Label>
            <Form.Control
              name="address"
              value={form.address}
              onChange={handleChange}
              required
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>City <span className="text-danger">*</span></Form.Label>
            <Form.Control
              name="city"
              value={form.city}
              onChange={handleChange}
              required
            />
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>State <span className="text-danger">*</span></Form.Label>
            <Form.Control
              name="state"
              value={form.state}
              onChange={handleChange}
              required
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Country <span className="text-danger">*</span></Form.Label>
            <Form.Control
              name="country"
              value={form.country}
              onChange={handleChange}
              required
            />
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Landmark</Form.Label>
            <Form.Control
              name="landmark"
              value={form.landmark}
              onChange={handleChange}
            />
          </Col>
        </Row>

        <h4 className="register-company-section-header">Category Details</h4>
        <Row>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Category <span className="text-danger">*</span></Form.Label>
            <Form.Select
              name="category_id"
              value={form.category_id}
              onChange={handleChange}
              required
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Subcategory <span className="text-danger">*</span></Form.Label>
            <Form.Select
              name="subcategory_id"
              value={form.subcategory_id}
              onChange={handleChange}
              required
              disabled={!form.category_id}
            >
              <option value="">Select subcategory</option>
              {subcategories.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Company Logo</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </Col>
        </Row>

        <div className="d-flex justify-content-end mt-4">
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              "Register Company"
            )}
          </Button>
        </div>
      </Form>
    </Container>
  );
}